import clsx from 'clsx'
import type {
  AriaAttributes,
  KeyboardEventHandler,
  MouseEventHandler,
  PropsWithChildren,
  Ref,
} from 'react'
import React, { forwardRef } from 'react'
import { toDataSet } from '../utilities/toDataSet'

export interface IconButtonProps {
  'aria-label': AriaAttributes['aria-label']
  'aria-controls'?: AriaAttributes['aria-controls']
  'aria-expanded'?: AriaAttributes['aria-expanded']
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  color?: IconButtonColor
  dataSet?: Record<string, string | undefined>
  disabled?: boolean
  id?: string
  onClick?: MouseEventHandler<HTMLButtonElement>
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>
  size?: IconButtonSize
  tabIndex?: number
  type?: IconButtonType
}

type IconButtonColor =
  | 'danger'
  | 'default'
  | 'inverse'
  | 'primary'
  | 'secondary'

type IconButtonColors = {
  [key in IconButtonColor]: string
}

type IconButtonSize = 'large' | 'medium' | 'small'

type IconButtonSizes = {
  [key in IconButtonSize]: string
}

type IconButtonType = 'button' | 'reset' | 'submit'

const colors: IconButtonColors = {
  danger: `text-error-main hover:bg-error-10 focus:bg-error-10 focus-visible:ring active:bg-error-20 disabled:text-black-25`,
  default: `text-black-60 hover:bg-black-5 focus:bg-black-5 focus-visible:ring active:bg-black-15 disabled:text-black-25`,
  inverse: `text-white-100 hover:bg-white-25 focus:bg-white-25 focus-visible:ring active:bg-white-45 disabled:text-white-30`,
  primary: `text-primary-main hover:bg-primary-10 focus:bg-primary-10 focus-visible:ring active:bg-primary-20 disabled:text-black-25`,
  secondary: `text-secondary-main hover:bg-secondary-10 focus:bg-secondary-10 focus-visible:ring active:bg-secondary-20 disabled:text-black-25`,
}

const sizes: IconButtonSizes = {
  large: 'w-15 h-15 p-3',
  medium: 'w-12 h-12 p-3',
  small: 'w-7 h-7 p-0.5',
}

export function Component(
  {
    'aria-label': label,
    'aria-controls': controls,
    'aria-expanded': expanded,
    'aria-haspopup': hasPopup,
    children,
    color = 'default',
    dataSet,
    disabled = false,
    id,
    onClick,
    onKeyDown,
    size = 'small',
    tabIndex = 0,
    type = 'button',
  }: PropsWithChildren<IconButtonProps>,
  ref?: Ref<HTMLButtonElement>,
): JSX.Element {
  return (
    <button
      aria-controls={controls}
      aria-expanded={expanded}
      aria-haspopup={hasPopup}
      aria-label={label}
      className={clsx(
        'flex-shrink-0 rounded-full disabled:bg-transparent disabled:cursor-not-allowed focus:outline-none',
        colors[color],
        sizes[size],
      )}
      {...toDataSet(dataSet)}
      disabled={disabled}
      id={id}
      onClick={onClick}
      onKeyDown={onKeyDown}
      ref={ref}
      tabIndex={tabIndex}
      type={type}
    >
      {children}
    </button>
  )
}

const IconButton = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<IconButtonProps>
>(Component)

IconButton.displayName = 'IconButton'

export default IconButton
